import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { DashboardData, PublishedGameSummary, LiveGameSummary, Announcement } from '../types';
import { getContrastColor } from '../utils/colorUtils';

export function Dashboard() {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const baseUrl = import.meta.env.VITE_API_URL || '';
        const response = await fetch(`${baseUrl}/api/dashboard`);
        if (!response.ok) {
          throw new Error('Failed to fetch dashboard data');
        }
        const data = await response.json();
        setDashboardData(data);
        setError(null);
      } catch (err) {
        console.error('Dashboard fetch error:', err);
        setError('Unable to load dashboard. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();

    // Refresh every 30 seconds
    const interval = setInterval(fetchDashboard, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <div className="animate-spin text-4xl mb-4">🏀</div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">

      <main className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Announcement Section - always shown if present */}
        {dashboardData?.announcement && (
          <AnnouncementCard announcement={dashboardData.announcement} />
        )}

        {/* Live Games Section */}
        {dashboardData?.liveGames && dashboardData.liveGames.length > 0 ? (
          <section>
            <h2 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <span className="animate-pulse inline-block w-2 h-2 bg-red-600 rounded-full"></span>
              Live Now
            </h2>
            <div className="space-y-3">
              {dashboardData.liveGames.map((game) => (
                <LiveGameCard key={game.gameId} game={game} />
              ))}
            </div>
          </section>
        ) : (
          <NoLiveGameCard />
        )}

        {/* Published Games Section */}
        <section>
          <h2 className="text-lg font-semibold text-gray-700 mb-3">Recent Games</h2>
          {dashboardData?.publishedGames && dashboardData.publishedGames.length > 0 ? (
            <div className="space-y-3">
              {dashboardData.publishedGames.map((game) => (
                <PublishedGameCard key={game.gameId} game={game} />
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-6 text-center">
              <p className="text-gray-500">No games have been published yet.</p>
            </div>
          )}
        </section>
      </main>

    </div>
  );
}

function LiveGameCard({ game }: { game: LiveGameSummary }) {
  return (
    <Link to={`/live/${game.gameId}`} className="block">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
        {/* Live indicator */}
        <div className="bg-red-600 text-white px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="animate-pulse inline-block w-2 h-2 bg-white rounded-full"></span>
            <span className="font-semibold">LIVE NOW</span>
          </div>
          <div className="text-sm">
            {game.viewerCount} {game.viewerCount === 1 ? 'viewer' : 'viewers'}
          </div>
        </div>

        {/* Team color bar */}
        <div className="flex h-1">
          <div className="flex-1" style={{ backgroundColor: game.awayTeam.primaryColor }} />
          <div className="flex-1" style={{ backgroundColor: game.homeTeam.primaryColor }} />
        </div>

        {/* Score display */}
        <div className="p-4">
          <div className="flex items-center justify-center gap-6">
            {/* Away team */}
            <div className="text-center flex-1">
              <div
                className="text-4xl font-bold mb-1 px-4 py-2 rounded-lg"
                style={{
                  backgroundColor: game.awayTeam.primaryColor,
                  color: getContrastColor(game.awayTeam.primaryColor),
                }}
              >
                {game.awayTeam.score}
              </div>
              <div className="font-medium text-gray-700">{game.awayTeam.abbreviation}</div>
              <div className="text-sm text-gray-500 truncate">{game.awayTeam.name}</div>
            </div>

            {/* Period / Clock / VS */}
            <div className="text-center">
              <div className="text-gray-500 text-xs font-medium uppercase">
                {game.period > 4 ? `OT${game.period - 4}` : `Period ${game.period}`}
              </div>
              {game.clockTime && (
                <div className="text-gray-700 text-lg font-mono font-bold">
                  {game.clockTime}
                </div>
              )}
              <div className="text-sm text-gray-300">vs</div>
            </div>

            {/* Home team */}
            <div className="text-center flex-1">
              <div
                className="text-4xl font-bold mb-1 px-4 py-2 rounded-lg"
                style={{
                  backgroundColor: game.homeTeam.primaryColor,
                  color: getContrastColor(game.homeTeam.primaryColor),
                }}
              >
                {game.homeTeam.score}
              </div>
              <div className="font-medium text-gray-700">{game.homeTeam.abbreviation}</div>
              <div className="text-sm text-gray-500 truncate">{game.homeTeam.name}</div>
            </div>
          </div>

          <div className="mt-4 text-center">
            <span className="inline-flex items-center gap-2 text-blue-600 font-medium">
              Watch Live
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

function NoLiveGameCard() {
  return (
    <div className="bg-white rounded-lg shadow p-6 text-center">
      <div className="text-4xl mb-3">📺</div>
      <h2 className="text-lg font-semibold text-gray-700 mb-2">No Live Game</h2>
      <p className="text-gray-500">Check back later for live game updates.</p>
    </div>
  );
}

function AnnouncementCard({ announcement }: { announcement: Announcement }) {
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg shadow p-4">
      <div className="flex items-start gap-3">
        <span className="text-2xl">📢</span>
        <div className="flex-1">
          <p className="text-blue-800 font-medium">{announcement.message}</p>
          <p className="text-blue-600 text-xs mt-2">
            Updated: {new Date(announcement.updatedAt).toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  );
}

function PublishedGameCard({ game }: { game: PublishedGameSummary }) {
  const gameDate = new Date(game.gameDate).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const getPeriodLabel = (period: number): string => {
    if (period <= 4) return 'Final';
    return `Final/OT${period - 4 > 1 ? period - 4 : ''}`;
  };

  return (
    <Link to={`/game/${game.gameId}`} className="block">
      <div className="bg-white rounded-lg shadow overflow-hidden hover:shadow-md transition-shadow">
        {/* Team color bar */}
        <div className="flex h-1">
          <div className="flex-1" style={{ backgroundColor: game.awayTeam.primaryColor }} />
          <div className="flex-1" style={{ backgroundColor: game.homeTeam.primaryColor }} />
        </div>

        <div className="p-4">
          <div className="flex items-center justify-between">
            {/* Teams and scores */}
            <div className="flex items-center gap-4">
              {/* Away team */}
              <div className="text-center min-w-[60px]">
                <div className="text-2xl font-bold text-gray-900">
                  {game.awayTeam.score}
                </div>
                <div className="text-xs text-gray-500">{game.awayTeam.abbreviation}</div>
              </div>

              <div className="text-gray-300">vs</div>

              {/* Home team */}
              <div className="text-center min-w-[60px]">
                <div className="text-2xl font-bold text-gray-900">
                  {game.homeTeam.score}
                </div>
                <div className="text-xs text-gray-500">{game.homeTeam.abbreviation}</div>
              </div>

              {/* Game info */}
              <div className="border-l pl-4 ml-2">
                <div className="text-sm font-medium text-gray-700">
                  {game.awayTeam.name} @ {game.homeTeam.name}
                </div>
                <div className="text-xs text-gray-500">{gameDate}</div>
              </div>
            </div>

            {/* Status */}
            <div className="text-right">
              <span className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                {getPeriodLabel(game.finalPeriod)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
